import { useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useRouter } from 'next/router';

import clsx from 'clsx';

import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
// Hooks
import useAlbums from '@/utils/hooks/swr/albums/useAlbums';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Components
import InfoBanner from '@/components/base/InfoBanner';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';
import ErrorDefaultFallback from '@/components/layouts/ErrorFallbacks/ErrorDefaultFallback';
import AlbumCard from '@/components/pages/albums/AlbumCard';
import AlbumEmpty from '@/components/pages/albums/Albums/AlbumsEmpty';
import AlbumsFeedPlaceholder from '@/components/pages/albums/Albums/AlbumsFeedPlaceholder';
import AlbumsSearch from '@/components/pages/albums/Albums/AlbumsSearch';
import AlbumsSearchNoData from '@/components/pages/albums/Albums/AlbumsSearchNoData';

// Utils
import { CONTENT_ALBUM_ANALYTICS } from '@/routes/privateRoutes';

export const defaultAlbumFeedGridStyle =
    'grid w-full grid-cols-12 gap-x-5 gap-y-12 3xl:grid-cols-10';

export const defaultTreeStructureAlbumFeedGridStyle =
    'grid w-full grid-cols-12 gap-5 @7xl/wrapper-platform-content:grid-cols-10';

export const defaultAlbumCardStyle =
    'col-span-full xs:col-span-6 lg:col-span-4 2xl:col-span-3 3xl:col-span-2';

export const defaultTreeStructureAlbumCardStyle =
    'col-span-full @md/wrapper-platform-content:col-span-6 @3xl/wrapper-platform-content:col-span-4 @5xl/wrapper-platform-content:col-span-3 @7xl/wrapper-platform-content:col-span-2';

type FeedProps = Pick<
    ReturnType<typeof useAlbums>,
    'albums' | 'isLoadMore' | 'isLoadingMore' | 'isLoadingInitialData' | 'loadMoreAlbums'
>;

const Feed = ({
    albums,
    isLoadMore,
    // Loaders
    isLoadingMore,
    isLoadingInitialData,
    // Functions
    loadMoreAlbums
}: FeedProps) => {
    const router = useRouter();

    if (isLoadingInitialData) {
        return <AlbumsFeedPlaceholder />;
    }

    if (!albums?.[0]?.data?.length) {
        return <AlbumsSearchNoData />;
    }

    return (
        <>
            <main className={defaultTreeStructureAlbumFeedGridStyle}>
                {albums?.map((page) => {
                    return page?.data?.map((album) => (
                        <AlbumCard
                            onClick={() => {
                                if (album?.id) {
                                    router.push(
                                        CONTENT_ALBUM_ANALYTICS.replace(
                                            '[albumId]',
                                            album.id.toString()
                                        )
                                    );
                                }
                            }}
                            key={album.id}
                            className={defaultTreeStructureAlbumCardStyle}
                            type={album.type}
                            title={album.name}
                            countMedias={album.countMedias}
                            medias={album.medias}
                            analytics={album.analytics}
                            criteria={album.criteria}
                        />
                    ));
                })}
            </main>
            {isLoadMore ? (
                <div className="mt-6 flex justify-center">
                    <LoadMoreButton isLoadingMore={isLoadingMore} loadMoreItems={loadMoreAlbums} />
                </div>
            ) : null}
        </>
    );
};

const AlbumsFeed = () => {
    const { t } = useTranslation();
    const { activeSocialProfile } = useActiveSocialProfile();

    // Search
    const cancelTokenRef = useRef<AbortController>(null);
    const [albumSearch, setAlbumSearch] = useState<string>(undefined);

    const {
        albums,
        albumsError,
        isLoadMore,
        // Loaders
        isLoadingMore,
        isLoadingInitialData,
        // Functions
        mutateAlbums,
        loadMoreAlbums
    } = useAlbums({
        activeSPID: activeSocialProfile?.id,
        limit: 10,
        params: { name: albumSearch },
        cancelToken: cancelTokenRef.current,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false }
    });

    if (isLoadingInitialData && !albumSearch) {
        return (
            <div>
                <div className="mb-5 h-8 max-w-52 rounded-full bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
                <AlbumsFeedPlaceholder />
            </div>
        );
    }

    if (albumsError && !albumSearch) {
        return (
            <>
                <InfoBanner
                    title={t('errors:error_occured')}
                    message={t('errors:error_occured_fetch_data')}
                />
                <AlbumsSearchNoData />
            </>
        );
    }

    if (!albums?.[0]?.data?.length && !albumSearch && !isLoadingInitialData) {
        return <AlbumEmpty />;
    }

    return (
        <>
            <ErrorBoundary
                FallbackComponent={ErrorDefaultFallback}
                onReset={() => mutateAlbums(null)}>
                {albumsError ? (
                    <InfoBanner
                        className="mb-4"
                        title={t('errors:error_occured')}
                        message={t('errors:error_occured_fetch_data')}
                    />
                ) : null}
                <AlbumsSearch cancelTokenRef={cancelTokenRef} setAlbumSearch={setAlbumSearch} />
                <Feed
                    albums={albums}
                    isLoadMore={isLoadMore}
                    isLoadingMore={isLoadingMore}
                    loadMoreAlbums={loadMoreAlbums}
                    isLoadingInitialData={isLoadingInitialData}
                />
            </ErrorBoundary>
        </>
    );
};

export default AlbumsFeed;
